import { useToast } from '@chakra-ui/react';
import {
   convertCartLineItemsToAnalyticsItem,
   trackInAnalyticsRemoveFromCart,
   trackPiwikCartUpdate,
} from '@ifixit/analytics';
import { SentryError } from '@ifixit/sentry';
import { useShopifyStorefrontClient } from '@ifixit/shopify-storefront-client';
import { useIsMutating, useMutation, useQueryClient } from '@tanstack/react-query';
import { removeCartLines } from '../../helpers/storefront-api';
import { buildIfixitCart } from '../../models/cart';
import type { Cart, CartLineItem } from '../../types';
import { CART_MUTATION_KEY, cartKeys } from '../../utils';
import { useCart } from './use-cart';
import { useTranslations } from '@ifixit/i18n';
import { StorefrontApiValidationError } from '../../helpers/validationError';

export type RemoveLineItemInput = {
   line: CartLineItem;
   analytics: {
      localeCode: string;
   };
};

const META_TYPE = 'remove-line-item';

export function useRemoveLineItem() {
   const cart = useCart().data;
   const isMutating = useIsMutating({
      mutationKey: CART_MUTATION_KEY,
      predicate: mutation => mutation.meta?.type !== META_TYPE,
   });
   const queryClient = useQueryClient();
   const { client: storefrontClient, currencyCode } = useShopifyStorefrontClient();
   const toast = useToast();
   const t = useTranslations();
   const mutation = useMutation({
      mutationKey: CART_MUTATION_KEY,
      meta: { type: META_TYPE },
      mutationFn: async ({ line }: RemoveLineItemInput) => {
         if (cart == null || !cart.shopifyCartId || !line?.shopifyLineId) {
            return buildIfixitCart({ cart: null, fallbackCurrencyCode: currencyCode });
         }
         return buildIfixitCart({
            cart: await removeCartLines(storefrontClient, {
               cartId: cart.shopifyCartId,
               lineIds: [line.shopifyLineId],
            }),
            fallbackCurrencyCode: currencyCode,
         });
      },
      onMutate: async (input: RemoveLineItemInput) => {
         await queryClient.cancelQueries({ queryKey: cartKeys.cart });

         const previousCart = queryClient.getQueryData<Cart>(cartKeys.cart);

         queryClient.setQueryData<Cart | undefined>(cartKeys.cart, current => {
            if (current == null) {
               return current;
            }
            const deletedItem = current.lineItems.find(
               item => item.itemcode === input.line.itemcode
            );
            if (deletedItem == null) {
               return current;
            }
            const updatedItemCount = Math.max(current.totals.itemsCount - deletedItem.quantity, 0);
            const updateTotalPrice = Math.max(
               Number(current.totals.price.amount) -
                  deletedItem.quantity * Number(deletedItem.price.amount),
               0
            ).toFixed(2);
            return {
               ...current,
               hasItemsInCart: updatedItemCount > 0,
               isEmpty: updatedItemCount === 0,
               lineItems: current.lineItems.filter(
                  product => product.itemcode !== input.line.itemcode
               ),
               totals: {
                  ...current.totals,
                  price: {
                     ...current.totals.price,
                     amount: updateTotalPrice,
                  },
                  itemsCount: updatedItemCount,
               },
            };
         });

         return { previousCart };
      },
      onError: (error, variables, context) => {
         queryClient.setQueryData<Cart | undefined>(cartKeys.cart, context?.previousCart);
         toast.closeAll();
         let validationErrors: string[] = [];
         if (error instanceof StorefrontApiValidationError) {
            validationErrors = error.failedValidations;
         } else if (
            error instanceof SentryError &&
            Array.isArray(error.sentryDetails.extra?.validationErrors)
         ) {
            validationErrors = error.sentryDetails.extra.validationErrors;
         }
         if (validationErrors.length) {
            validationErrors.forEach(error =>
               toast({
                  id: 'remove-line-item-error',
                  status: 'error',
                  title: error,
                  isClosable: true,
                  variant: 'subtle',
                  position: 'bottom-right',
               })
            );
         } else {
            toast({
               id: 'remove-line-item-error',
               status: 'error',
               title: t('ErrorHandling.unableToRemoveProduct'),
               description: t('ErrorHandling.tryActionAgain'),
               isClosable: true,
               variant: 'subtle',
               position: 'bottom-right',
            });
         }
      },
      onSuccess: (cart, { line, analytics }) => {
         trackInAnalyticsRemoveFromCart({
            items: convertCartLineItemsToAnalyticsItem([line]),
            value: line.quantity * Number(line.price.amount),
            currency: line.price.currencyCode,
            localeCode: analytics.localeCode,
         });
         trackPiwikCartUpdate({
            items: convertCartLineItemsToAnalyticsItem(cart.lineItems),
            value: Number(cart.totals.price.amount),
            currency: cart.totals.price.currencyCode,
            localeCode: analytics.localeCode,
         });
      },
      onSettled: () => queryClient.invalidateQueries({ queryKey: cartKeys.cart }),
   });
   return { removeLineItem: mutation, enabled: cart != null && !isMutating };
}
